import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '../toast.service';
import { AssetsDataService } from '../assets-data.service';
import { SubmitStatusService, SubmitFailureEvent, buildRetryRequest } from './submit-status.service';
import { JOB_LABELS } from '../../models/logs/SubmitJob';

/** Wording used in the escalated toast message (paired with the "Email IT" button). */
const ESCALATION_CONTACT = 'email the IT Service Desk';
/** Failures for the same delivery at or above this count get the escalated wording + email. */
const ESCALATE_AT = 2;

/** A short display name for a delivery ("PO 544333", or a fallback naming the log). */
function subjectFor(event: SubmitFailureEvent): string {
  return event.poNumber ? `PO ${event.poNumber}` : `receiving log ${event.receivingLogId}`;
}

/**
 * Builds a `mailto:` link to the IT Service Desk (addresses come from app settings),
 * pre-filled with what failed so the user only has to review and send (nothing is sent
 * automatically). CC'd to the oversight address when configured. No attachments — the
 * photo bytes are safe on the server; this is a text report about the failed follow-up step.
 */
export function buildEscalationMailto(event: SubmitFailureEvent, toEmail: string, ccEmail?: string): string {
  const subject = subjectFor(event);
  const lines = [
    `There has been an error completing a receiving log submission in the ReceivingMgr`,
    `mobile app that is still failing after a retry.`,
    ``,
    `The delivery record itself saved OK — it's the follow-up step(s) below that keep`,
    `failing and need a look:`,
    ``,
    `PO: ${event.poNumber ?? '(none)'}`,
    `Log ID: ${event.receivingLogId}`,
    `Failing step(s):`,
    ...event.failedJobs.map((job) => {
      const label = JOB_LABELS[job.type] ?? job.type;
      const err = job.lastError ? ` — ${job.lastError}` : '';
      return `  • ${label}${err} (${job.attempts}/${job.maxAttempts} attempts)`;
    }),
    ``,
    `Sent from the ReceivingMgr mobile app.`,
  ];
  const mailSubject = `ReceivingMgr mobile — receiving log error persisting after retry — ${subject} (log ${event.receivingLogId})`;
  return `mailto:${toEmail}`
    + `?${ccEmail ? `cc=${encodeURIComponent(ccEmail)}&` : ''}subject=${encodeURIComponent(mailSubject)}`
    + `&body=${encodeURIComponent(lines.join('\n'))}`;
}

/**
 * App-level listener that turns a background-job permanent failure into a toast with a
 * one-tap Retry, so a failure is surfaced wherever the user is — not only while they're
 * on the Done screen (which shows its own inline banner and is suppressed via
 * `setActiveLog`). It also:
 *  - confirms a **successful** retry ("… re-sent ✓"), the only feedback the user gets
 *    once they've left the Done screen;
 *  - **escalates** after a delivery fails again despite a retry: the wording points the
 *    user at IT and an **Email IT** button opens a pre-filled report to the Service Desk.
 *
 * `init()` is called once from AppComponent; the poll/retry plumbing lives in
 * SubmitStatusService, so this only maps events to toasts.
 */
@Injectable({ providedIn: 'root' })
export class SubmitFailureNotifierService implements OnDestroy {
  private readonly _subs = new Subscription();
  private _started = false;

  /** Deliveries the user retried from a toast, keyed to their display label, so a later
   *  `Complete` can be confirmed as "re-sent" (and normal completions stay silent). */
  private readonly _retried = new Map<number, string>();
  /** Times each delivery has failed (permanently) this session — drives escalation. */
  private readonly _counts = new Map<number, number>();

  constructor(
    private _submitStatus: SubmitStatusService,
    private _toast: ToastService,
    private _assets: AssetsDataService
  ) {}

  /** Starts listening. Idempotent. */
  public init(): void {
    if (this._started) { return; }
    this._started = true;
    this._subs.add(this._submitStatus.needsAttention$.subscribe((event) => this._onFailure(event)));
    this._subs.add(this._submitStatus.completed$.subscribe((logId) => this._onCompleted(logId)));
  }

  private _onFailure(event: SubmitFailureEvent): void {
    const subject = subjectFor(event);
    const count = (this._counts.get(event.receivingLogId) ?? 0) + 1;
    this._counts.set(event.receivingLogId, count);

    const actions: { text: string; handler: () => void }[] = [
      { text: 'Retry', handler: () => this._retry(event, subject) },
    ];

    let message: string;
    if (count >= ESCALATE_AT) {
      // Failed again despite a retry — stop implying another tap will fix it, and offer
      // a pre-filled report to IT.
      message = `${subject} still couldn't send after a retry — please ${ESCALATION_CONTACT}, or try again.`;
      actions.push({ text: 'Email IT', handler: () => this._emailIt(event) });
    } else {
      const jobs = event.failedJobs.map((job) => JOB_LABELS[job.type] ?? job.type).join(', ');
      message = `${subject} couldn't finish: ${jobs}. The delivery is saved — re-send when ready.`;
    }

    void this._toast.showActionMessage(message, actions);
  }

  private _retry(event: SubmitFailureEvent, subject: string): void {
    this._retried.set(event.receivingLogId, subject);
    // Re-runs the failed jobs; if a photo job is among them and the log had an email,
    // that email is re-sent (force) so it goes out with the now-uploaded photos.
    const { jobTypes, force } = buildRetryRequest(event.failedJobs, event.allJobs);
    this._submitStatus.retry(event.receivingLogId, jobTypes, force).subscribe({
      error: () => {
        this._retried.delete(event.receivingLogId);
        this._toast.showWarningMessage('Could not re-send. Please try again.');
      },
    });
  }

  private _emailIt(event: SubmitFailureEvent): void {
    // Opens the user's mail app with a pre-filled draft (addresses from app settings);
    // the user reviews and sends.
    const emails = this._assets.GetAppEmailSettings();
    window.location.href = buildEscalationMailto(event, emails.itServiceDesk, emails.itServiceDeskCc);
  }

  private _onCompleted(logId: number): void {
    const subject = this._retried.get(logId);
    // Only confirm completions the user actually retried; normal submits complete quietly.
    if (!subject) { return; }
    this._retried.delete(logId);
    this._counts.delete(logId);
    void this._toast.showSuccessMessage(`${subject} — re-sent ✓`);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
